// ─────────────────────────────────────────────────────────────────────────────
// THE PEOPLE AT THIS FIRM, AND THE INVITES STILL OUT.
//
// ⚠️ THIS IS COLLEAGUE DATA AND THE RULE IS NOT THE BORROWER RULE. "Progress,
// never contents" protects BORROWERS; these rows are the firm's own staff, and a
// broker may see who is on their account and how far through onboarding each
// person is. What stays out is anything personal that is not about working at the
// firm — no banking, no license numbers, no documents. The endpoint selects none
// of them, so there is nothing here to render by accident.
//
// 🚨 PENDING INVITES ARE LISTED BESIDE THE MEMBERS, and that is not decoration.
// One pending invite per email is a database constraint; a broker who cannot see
// the invite they sent yesterday sends it again and is refused with an error
// about a duplicate they have no way to look at.
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import DoorlyProLayout, { RequirePartnerAuth } from '../../components/DoorlyProLayout';
import { useDoorlyProAuth } from '../../context/DoorlyProAuthContext';
import { Modal } from '../../components/doorlypro/BorrowerConsentCell';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const STATUS_LABEL = {
  onboarding: 'Setting up',
  email_verified: 'Verifying email',
  training_pending: 'In training',
  active: 'Active',
  archived: 'Removed',
};

const inputStyle = {
  width: '100%', padding: '10px 12px', borderRadius: 8,
  border: '1px solid #e5e7eb', fontSize: 14, outline: 'none', background: '#fff',
};

function DoorlyProFirmTeam() {
  const { token, partner } = useDoorlyProAuth();
  const [members, setMembers] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [inviting, setInviting] = useState(false);
  const [notice, setNotice] = useState('');
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', partner_type: '' });
  // 🔑 THE OWNER'S TEAM CONTROLS (broker gaps, Jesse 2026-11-02): Remove from team, Resend invite, Cancel invite. Shown
  // while the server's switch is on (manage_enabled — backend constants/brokerGapsFlags FIRM_TEAM_MANAGE_CONTROLS, on by
  // default since Hannah approved the wording 2026-11-03; 'off' is the kill switch). The server decides who may do what.
  const [manageEnabled, setManageEnabled] = useState(false);
  const [confirm, setConfirm] = useState(null); // { kind: 'remove'|'cancel', id, label }
  const [busy, setBusy] = useState(false);
  const [confirmError, setConfirmError] = useState('');

  const load = useCallback(() => {
    if (!token) return;
    axios.get(`${API_URL}/api/doorlypro/firm/members`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        setMembers(res.data?.members || []); setPending(res.data?.pendingInvites || []);
        setManageEnabled(!!res.data?.manage_enabled);
      })
      .catch(() => setError('This view isn’t available on your account.'))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(load, [load]);

  // ⚠️ SEEDED FROM THE BROKER'S OWN TYPE, NOT HARDCODED. A realtor brokerage
  // invites realtors and a mortgage company invites loan officers; making them
  // pick the obvious answer every time is how the wrong one gets chosen.
  useEffect(() => {
    if (!form.partner_type && partner?.partner_type) {
      setForm((f) => ({ ...f, partner_type: partner.partner_type }));
    }
  }, [partner, form.partner_type]);

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setNotice('');
    if (!form.first_name || !form.last_name || !form.email) {
      setError('First name, last name and email are required.');
      return;
    }
    setInviting(true);
    try {
      const res = await axios.post(`${API_URL}/api/doorlypro/firm/invite`, form,
        { headers: { Authorization: `Bearer ${token}` } });
      // ⚠️ THE OWNERSHIP OUTCOME IS NOT SHOWN TO THE BROKER. owner_basis tells
      // DOORLY whether a rep was assigned; "no Partner Success owner" is an
      // internal staffing fact and means nothing to the firm, who would only
      // wonder what they had done wrong. The invite succeeded either way.
      if (res.data?.success) {
        setNotice(`Invite sent to ${form.email}.`);
        setForm({ first_name: '', last_name: '', email: '', phone: '', partner_type: form.partner_type });
        load();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Could not send that invite.');
    } finally {
      setInviting(false);
    }
  };

  const resendInvite = async (inv) => {
    setError(''); setNotice('');
    try {
      const res = await axios.post(`${API_URL}/api/doorlypro/firm/invites/${inv.id}/resend`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      if (res.data?.ok) { setNotice(`Invite re-sent to ${res.data.sent_to || inv.email}.`); load(); }
    } catch (err) {
      setError(err.response?.data?.error || 'Could not send that invite.');
    }
  };

  const confirmAction = async () => {
    if (!confirm) return;
    setBusy(true); setConfirmError('');
    const url = confirm.kind === 'remove'
      ? `${API_URL}/api/doorlypro/firm/members/${confirm.id}/remove`
      : `${API_URL}/api/doorlypro/firm/invites/${confirm.id}/cancel`;
    try {
      const res = await axios.post(url, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data?.ok) {
        setNotice(confirm.kind === 'remove' ? `${confirm.label} was removed from your team.` : `Invite to ${confirm.label} cancelled.`);
        setConfirm(null);
        load();
      }
    } catch (err) {
      setConfirmError(err.response?.data?.error || 'That didn’t work. Nothing was changed.');
    } finally {
      setBusy(false);
    }
  };

  const linkBtn = { border: 'none', background: 'none', padding: 0, color: '#3a6ad9', fontSize: 12, fontWeight: 700, cursor: 'pointer', textDecoration: 'underline', whiteSpace: 'nowrap' };
  const myId = partner?.id;

  const card = { backgroundColor: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' };

  return (
    <RequirePartnerAuth>
      <DoorlyProLayout>
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#111827', margin: '0 0 4px' }}>Your Team</h1>
          <p style={{ fontSize: 14, color: '#6b7280', margin: 0 }}>
            Everyone at your firm on DoorlyPro, and anyone you’ve invited.
          </p>
        </div>

        {error && (
          <div style={{ marginBottom: 16, padding: '12px 16px', background: '#fff0f3', color: '#ff082f', fontSize: 13, borderRadius: 10 }}>{error}</div>
        )}
        {notice && (
          <div style={{ marginBottom: 16, padding: '12px 16px', background: '#f0fdf4', color: '#166534', fontSize: 13, borderRadius: 10 }}>{notice}</div>
        )}

        {/* ── members ─────────────────────────────────────────────────────── */}
        <div style={{ ...card, marginBottom: 20 }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>Loading your team...</div>
          ) : members.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#6b7280', fontSize: 14 }}>
              Nobody else is on your firm’s account yet.
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                    {['Name', 'Email', 'Status', partner?.partner_type === 'loan_officer' ? 'Loan files' : 'Buyers', 'Joined'].map((c) => (
                      <th key={c} style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 12, whiteSpace: 'nowrap' }}>{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {members.map((m, i) => (
                    <tr key={m.id} style={{ borderBottom: i < members.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                      <td style={{ padding: '12px 16px', color: '#111827', fontWeight: 600 }}>
                        {`${m.first_name || ''} ${m.last_name || ''}`.trim()}
                        {m.account_role === 'broker' && (
                          <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 6, background: '#edf1fb', color: '#3a6ad9' }}>
                            BROKER
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '12px 16px', color: '#4b5563' }}>{m.email}</td>
                      <td style={{ padding: '12px 16px', color: '#4b5563' }}>{STATUS_LABEL[m.onboarding_status] || m.onboarding_status}</td>
                      <td style={{ padding: '12px 16px', color: '#4b5563' }}>{m.buyers == null ? '—' : m.buyers}</td>
                      <td style={{ padding: '12px 16px', color: '#9ca3af', whiteSpace: 'nowrap' }}>
                        {m.created_at ? new Date(m.created_at).toLocaleDateString() : '—'}
                        {manageEnabled && m.account_role !== 'broker' && m.id !== myId && (
                          <div style={{ marginTop: 4 }}>
                            <button type="button" data-testid="firm-member-remove" style={{ ...linkBtn, color: '#b3321f' }}
                              onClick={() => { setConfirmError(''); setConfirm({ kind: 'remove', id: m.id, label: `${m.first_name || ''} ${m.last_name || ''}`.trim() || m.email }); }}>
                              Remove from team
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ── pending invites ─────────────────────────────────────────────── */}
        {pending.length > 0 && (
          <div style={{ ...card, marginBottom: 20 }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6', fontSize: 12, fontWeight: 700, color: '#6b7280', letterSpacing: '0.04em' }}>
              INVITED, NOT YET REGISTERED
            </div>
            {pending.map((p, i) => (
              <div key={p.id} style={{
                padding: '12px 16px', display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', gap: 12, fontSize: 13,
                borderBottom: i < pending.length - 1 ? '1px solid #f3f4f6' : 'none',
              }}>
                <span style={{ color: '#111827' }}>
                  {`${p.first_name || ''} ${p.last_name || ''}`.trim()} <span style={{ color: '#6b7280' }}>· {p.email}</span>
                </span>
                <span style={{ color: '#9ca3af', whiteSpace: 'nowrap', display: 'inline-flex', gap: 12, alignItems: 'center' }}>
                  sent {p.created_at ? new Date(p.created_at).toLocaleDateString() : '—'}
                  {manageEnabled && (
                    <>
                      <button type="button" data-testid="firm-invite-resend" style={linkBtn} onClick={() => resendInvite(p)}>
                        Resend invite
                      </button>
                      <button type="button" data-testid="firm-invite-cancel" style={{ ...linkBtn, color: '#6b7280' }}
                        onClick={() => { setConfirmError(''); setConfirm({ kind: 'cancel', id: p.id, label: p.email }); }}>
                        Cancel invite
                      </button>
                    </>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* ── invite ──────────────────────────────────────────────────────── */}
        <div style={card}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid #f3f4f6' }}>
            <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#111827' }}>Invite someone to your firm</h2>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>
              They’ll get an email to register. Their buyers stay theirs — this just puts them on your firm’s account.
            </p>
          </div>
          <form onSubmit={submit} style={{ padding: 16, display: 'grid', gap: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <input style={inputStyle} placeholder="First name" value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
              <input style={inputStyle} placeholder="Last name" value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
            </div>
            <input style={inputStyle} type="email" placeholder="Email address" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <input style={inputStyle} placeholder="Phone (optional)" value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            <button type="submit" disabled={inviting} style={{
              justifySelf: 'start', padding: '10px 20px', borderRadius: 10,
              backgroundColor: inviting ? '#9ca3af' : '#3a6ad9', color: '#fff',
              fontSize: 14, fontWeight: 700, border: 'none', cursor: inviting ? 'default' : 'pointer',
            }}>
              {inviting ? 'Sending…' : 'Send invite'}
            </button>
          </form>
        </div>
        {confirm && (
          <Modal
            title={confirm.kind === 'remove' ? `Remove ${confirm.label} from your team` : `Cancel the invite to ${confirm.label}`}
            onClose={() => { setConfirm(null); setConfirmError(''); }}>
            <p style={{ margin: 0, fontSize: 13, color: '#4b5563', lineHeight: 1.6 }}>
              {confirm.kind === 'remove'
                ? 'They lose access to DoorlyPro right away. The loan files they sent stay with your firm and stay on Team Pipeline.'
                : 'The link in their email stops working. You can invite them again later.'}
            </p>
            <div style={{ marginTop: 14, display: 'flex', gap: 10, alignItems: 'center' }}>
              <button type="button" data-testid="firm-team-confirm" onClick={confirmAction} disabled={busy}
                style={{ border: 'none', background: confirm.kind === 'remove' ? '#b3321f' : '#3a6ad9', color: '#fff', borderRadius: 8,
                  padding: '9px 16px', fontSize: 13, fontWeight: 700, cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.6 : 1 }}>
                {confirm.kind === 'remove' ? 'Remove' : 'Cancel invite'}
              </button>
              <button type="button" onClick={() => { setConfirm(null); setConfirmError(''); }}
                style={{ ...linkBtn, textDecoration: 'none', color: '#6b7280' }}>{confirm.kind === 'remove' ? 'Cancel' : 'Keep it'}</button>
            </div>
            {confirmError && (
              <p style={{ margin: '12px 0 0', fontSize: 12.5, lineHeight: 1.6, fontWeight: 600, color: '#b3321f' }}>{confirmError}</p>
            )}
          </Modal>
        )}
      </DoorlyProLayout>
    </RequirePartnerAuth>
  );
}

export default DoorlyProFirmTeam;
